import React, { useState, useEffect } from 'react';
import { Wallet, TrendingUp, ArrowUpRight, ArrowDownLeft, FileText, ExternalLink, RefreshCw, X } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { FinanceService } from '../services/financeService';
import { OperatorAccount, FinanceStats } from '../types';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

interface PayoutModalProps {
    operator: OperatorAccount;
    onClose: () => void;
    onSuccess: () => void;
}

const PayoutModal: React.FC<PayoutModalProps> = ({ operator, onClose, onSuccess }) => {
    const [amount, setAmount] = useState(operator.balance.toString());
    const [description, setDescription] = useState(`Payout to ${operator.operatorName}`);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const payoutAmount = parseFloat(amount);

        if (isNaN(payoutAmount) || payoutAmount <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        if (payoutAmount > operator.balance) {
            toast.error('Payout amount exceeds available balance');
            return;
        }

        setLoading(true);
        try {
            await FinanceService.createPayout(operator.id, payoutAmount, description);
            toast.success('Payout processed successfully');
            onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.message || 'Failed to process payout');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
            <Card className="w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold">Process Payout</h2>
                    <button onClick={onClose} className="text-secondary-400 hover:text-secondary-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="mb-6 p-4 bg-primary-50 rounded-xl">
                    <p className="text-sm text-primary-600 font-medium">{operator.operatorName}</p>
                    <p className="text-2xl font-bold text-primary-700">GHS {operator.balance.toLocaleString()}</p>
                    <p className="text-xs text-primary-500">Available Balance</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Payout Amount (GHS)</label>
                        <input
                            type="number"
                            step="0.01"
                            required
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            max={operator.balance}
                            className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Description</label>
                        <input
                            type="text"
                            required
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div className="flex space-x-3 pt-4">
                        <Button variant="outline" type="button" onClick={onClose} className="flex-1 py-3">Cancel</Button>
                        <Button type="submit" loading={loading} className="flex-1 py-3">Confirm Payout</Button>
                    </div>
                </form>
            </Card>
        </div>
    );
};

export const Finance: React.FC = () => {
    const [stats, setStats] = useState<FinanceStats | null>(null);
    const [operators, setOperators] = useState<OperatorAccount[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'operators' | 'transactions'>('overview');
    const [selectedOperator, setSelectedOperator] = useState<OperatorAccount | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const results = await Promise.allSettled([
                FinanceService.getFinanceStats(),
                FinanceService.getOperatorAccounts()
            ]);

            if (results[0].status === 'fulfilled') {
                setStats(results[0].value);
            } else {
                console.error('Failed to load finance stats:', results[0].reason);
                toast.error('Failed to load financial statistics');
            }

            if (results[1].status === 'fulfilled') {
                setOperators(results[1].value);
            } else {
                console.error('Failed to load operator accounts:', results[1].reason);
                // Don't show a second toast, just log it
            }
        } catch (error) {
            console.error('Unexpected error loading finance data:', error);
            toast.error('Failed to load financial records');
        } finally {
            setLoading(false);
        }
    };

    if (loading && !stats) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Financial Management</h1>
                    <p className="text-secondary-600 text-sm">Monitor revenue, manage operator payouts, and view reports</p>
                </div>
                <div className="flex items-center space-x-3">
                    <Button variant="outline" size="sm" onClick={loadData} className="flex items-center">
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button variant="outline" size="sm" className="flex items-center">
                        <FileText className="h-4 w-4 mr-2" />
                        Export
                    </Button>
                </div>
            </div>

            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-green-50 rounded-xl text-green-600">
                            <TrendingUp className="h-6 w-6" />
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-medium text-secondary-500">Total Revenue</p>
                            <h3 className="text-2xl font-bold text-secondary-900">GHS {stats?.totalRevenue.toLocaleString() || '0'}</h3>
                        </div>
                    </div>
                    <div className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full w-fit">
                        <ArrowUpRight className="h-3 w-3 mr-1" />
                        <span>Real-time tracking active</span>
                    </div>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-yellow-50 rounded-xl text-yellow-600">
                            <Wallet className="h-6 w-6" />
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-medium text-secondary-500">Pending Collections</p>
                            <h3 className="text-2xl font-bold text-secondary-900">GHS {stats?.pendingCollections.toLocaleString() || '0'}</h3>
                        </div>
                    </div>
                    <p className="text-xs text-secondary-400">Awaiting MoMo settlement</p>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-primary-50 rounded-xl text-primary-600">
                            <ArrowDownLeft className="h-6 w-6" />
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-medium text-secondary-500">Total Payouts</p>
                            <h3 className="text-2xl font-bold text-secondary-900">GHS {stats?.totalPayouts.toLocaleString() || '0'}</h3>
                        </div>
                    </div>
                    <p className="text-xs text-secondary-400">Total processed to operators</p>
                </Card>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 bg-secondary-100 p-1 rounded-xl w-fit">
                {(['overview', 'operators', 'transactions'] as const).map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === tab
                            ? 'bg-white text-primary-600 shadow-sm'
                            : 'text-secondary-500 hover:text-secondary-700'
                            }`}
                    >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            <div className="mt-6">
                {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="font-bold text-secondary-900">Top Operators</h3>
                                <Button variant="ghost" size="sm" onClick={() => setActiveTab('operators')}>View All</Button>
                            </div>
                            <div className="space-y-4">
                                {operators.sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, 5).map(op => (
                                    <div key={op.id} className="flex items-center justify-between p-3 bg-secondary-50 rounded-xl border border-secondary-100">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center font-bold text-primary-600 border border-secondary-200">
                                                {op.operatorName.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-bold text-secondary-900 text-sm">{op.operatorName}</p>
                                                <p className="text-[10px] text-secondary-500 uppercase font-bold tracking-wider">Revenue: GHS {op.totalRevenue.toLocaleString()}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs font-bold text-secondary-900">GHS {op.balance.toLocaleString()}</p>
                                            <p className="text-[10px] text-secondary-400">Balance</p>
                                        </div>
                                    </div>
                                ))}
                                {operators.length === 0 && <p className="text-center py-6 text-secondary-400 text-sm italic">No operator data found</p>}
                            </div>
                        </Card>

                        <Card className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="font-bold text-secondary-900">Recent Transactions</h3>
                                <Button variant="ghost" size="sm" onClick={() => setActiveTab('transactions')}>View All</Button>
                            </div>
                            <div className="space-y-3">
                                {stats?.recentTransactions.map(tx => (
                                    <div key={tx.id} className="flex items-center justify-between p-3 hover:bg-secondary-50 transition-colors rounded-xl border border-transparent hover:border-secondary-100 group">
                                        <div className="flex items-center space-x-3">
                                            <div className={`p-2 rounded-lg ${tx.type === 'collection' ? 'bg-green-100 text-green-600' :
                                                tx.type === 'payout' ? 'bg-primary-100 text-primary-600' :
                                                    'bg-red-100 text-red-600'
                                                }`}>
                                                {tx.type === 'collection' ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownLeft className="h-4 w-4" />}
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-secondary-900 line-clamp-1">{tx.description}</p>
                                                <p className="text-[10px] text-secondary-400">{format(tx.createdAt, 'MMM d, HH:mm')}</p>
                                            </div>
                                        </div>
                                        <div className="text-right flex items-center space-x-3">
                                            <div className="hidden group-hover:block">
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase transition-all ${tx.status === 'completed' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>
                                                    {tx.status}
                                                </span>
                                            </div>
                                            <p className={`text-sm font-bold ${tx.type === 'collection' ? 'text-green-600' : 'text-secondary-900'}`}>
                                                {tx.type === 'collection' ? '+' : '-'} GHS {tx.amount.toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                                {(!stats?.recentTransactions || stats.recentTransactions.length === 0) && (
                                    <p className="text-center py-6 text-secondary-400 text-sm italic">No recent transactions</p>
                                )}
                            </div>
                        </Card>
                    </div>
                )}

                {activeTab === 'operators' && (
                    <Card className="overflow-hidden border-none shadow-sm rounded-2xl">
                        <table className="min-w-full">
                            <thead>
                                <tr className="bg-secondary-50 border-b border-secondary-200">
                                    <th className="px-6 py-4 text-left text-xs font-bold text-secondary-500 uppercase tracking-wider">Operator</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-secondary-500 uppercase tracking-wider">Available Balance</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-secondary-500 uppercase tracking-wider">Total Revenue</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-secondary-500 uppercase tracking-wider">Last Payout</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-secondary-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-right text-xs font-bold text-secondary-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-secondary-100">
                                {operators.map(op => (
                                    <tr key={op.id} className="hover:bg-secondary-50/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 font-bold text-xs mr-3">
                                                    {op.operatorName.charAt(0)}
                                                </div>
                                                <div className="text-sm font-bold text-secondary-900">{op.operatorName}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-primary-600">GHS {op.balance.toLocaleString()}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-secondary-600">GHS {op.totalRevenue.toLocaleString()}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-xs text-secondary-500">{op.lastPaymentAt ? format(op.lastPaymentAt, 'MMM d, yyyy') : 'Never'}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-[10px] font-bold rounded-full uppercase ${op.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {op.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                disabled={op.balance <= 0}
                                                onClick={() => setSelectedOperator(op)}
                                                className="rounded-lg text-xs font-bold px-4"
                                            >
                                                Payout
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                )}

                {activeTab === 'transactions' && (
                    <div className="space-y-4">
                        {stats?.recentTransactions.map(tx => (
                            <div key={tx.id} className="flex items-center justify-between p-4 bg-white border border-secondary-200 rounded-2xl hover:shadow-md transition-shadow group">
                                <div className="flex items-center space-x-4">
                                    <div className={`p-4 rounded-xl ${tx.type === 'collection' ? 'bg-green-50 text-green-600' :
                                        tx.type === 'payout' ? 'bg-primary-50 text-primary-600' :
                                            'bg-red-50 text-red-600'
                                        }`}>
                                        {tx.type === 'collection' ? <ArrowUpRight className="h-6 w-6" /> : <ArrowDownLeft className="h-6 w-6" />}
                                    </div>
                                    <div>
                                        <p className="font-bold text-secondary-900">{tx.description}</p>
                                        <div className="flex items-center space-x-2 mt-1">
                                            <span className="text-[10px] text-secondary-500 font-bold uppercase tracking-wider">{format(tx.createdAt, 'MMMM d, yyyy • HH:mm')}</span>
                                            {tx.status === 'pending' && (
                                                <span className="h-1.5 w-1.5 rounded-full bg-yellow-500 animate-pulse"></span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-6">
                                    <div className="text-right">
                                        <p className={`text-xl font-black ${tx.type === 'collection' ? 'text-green-600' : 'text-secondary-900'}`}>
                                            {tx.type === 'collection' ? '+' : '-'} GHS {tx.amount.toLocaleString()}
                                        </p>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest ${tx.status === 'completed' ? 'bg-green-100 text-green-700' :
                                            tx.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                                'bg-red-100 text-red-700'
                                            }`}>
                                            {tx.status}
                                        </span>
                                    </div>
                                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ExternalLink className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Payout Modal */}
            {selectedOperator && (
                <PayoutModal
                    operator={selectedOperator}
                    onClose={() => setSelectedOperator(null)}
                    onSuccess={loadData}
                />
            )}
        </div>
    );
};
